"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { SettingsCopiesInterfaceData } from "@/types/settingsCopies.types";
import { apiSdkInstance } from "@/apiSdk/stageApiSdk.instance";
import { getDefaultDialect, getPlanId } from "@/utils/common.helpers";

type StringsContextType = {
  settingsCopies: SettingsCopiesInterfaceData | null;
  currentLanguage: string;
  setCurrentLang: (lang: string) => void;
};

const SettingsCopiesContext = createContext<StringsContextType | undefined>(
  undefined,
);

export function StringsProvider({
  lang,
  dialect,
  children,
  defaultSettingsCopies,
}: {
  lang: string;
  dialect?: string;
  children: React.ReactNode;
  defaultSettingsCopies?: SettingsCopiesInterfaceData;
}) {
  const [settingsCopies, setSettingsCopies] =
    useState<SettingsCopiesInterfaceData | null>(defaultSettingsCopies || null);
  const [currentLanguage, setCurrentLanguage] = useState(lang);

  useEffect(() => {
    setCurrentLanguage(lang);
  }, [lang]);

  // Only fetch client-side when no server-provided settingsCopies exists.
  // Dialect-specific strings are already fetched server-side in [lang]/[dialect]/layout.tsx,
  // so we no longer need to re-fetch based on dialect presence.
  useEffect(() => {
    if (!settingsCopies) {
      (async () => {
        const _settingsCopies = await apiSdkInstance.user.fetchSettingsCopies({
          lang,
          dialect: dialect || getDefaultDialect(),
          overridingPlanId: await getPlanId(),
        });
        setSettingsCopies(_settingsCopies);
      })();
    }
  }, []);

  const setCurrentLang = (newLang: string) => {
    setCurrentLanguage(newLang);
  };

  return (
    <SettingsCopiesContext.Provider
      value={{ settingsCopies, currentLanguage, setCurrentLang }}
    >
      {children}
    </SettingsCopiesContext.Provider>
  );
}

export function useSettingsCopies() {
  const context = useContext(SettingsCopiesContext);
  if (context === undefined) {
    throw new Error("useSettingsCopies must be used within a StringsProvider");
  }
  return context;
}
